import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { I18nModule, TranslationService, UrlModule } from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CheckoutStepType } from '@spartacus/checkout/base/root';
import { CheckoutStepService } from '@spartacus/checkout/base/components';
import { RouterModule } from '@angular/router';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { GenCartType } from 'src/app/generac/enums/cart-type.enum';
import { GenShippingAccountType } from 'src/app/generac/enums/shipping-account-type.enum';
import { GenCheckoutDeliveryGroupsComponent } from 'src/app/generac/shared/components/checkout-delivery-groups/checkout-delivery-groups.component';
import { GenShippingGroup, GenHandlingOption, GenShippingAccount } from 'src/app/generac/models/cart.model';
import { Card, CardModule } from '@spartacus/storefront';

@Component({
  selector: 'gen-shipping-groups',
  templateUrl: './shipping-groups.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CardModule,
    UrlModule,
    RouterModule,
    I18nModule,
    GenCheckoutDeliveryGroupsComponent,
  ]
})
export class GenShippingGroupsComponent {
  @Input() daFreeShipping: boolean;
  @Input() shippingGroups: GenShippingGroup[];
  @Input() isOrderConfirmPage: boolean = false;
  checkoutStepTypeServiceParts = CheckoutStepType.SERVICE_PARTS;
  checkoutStepTypeSpecialHandling = CheckoutStepType.SPECIAL_HANDLING;

  constructor(
    protected translationService: TranslationService,
    protected checkoutStepService: CheckoutStepService,
    private checkoutService: GenCheckoutService,
  ) {

  }

  getSpecialHandlingCard(handlingOptions: GenHandlingOption[]): Observable<Card> {
    return combineLatest([
      this.translationService.translate('checkoutProgress.specialHandlingTab'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: handlingOptions.map(option => option.name),
        } as Card;
      })
    );
  }

  getSpCarrierCard(shippingGroup: GenShippingGroup): Observable<Card> {
    const data = this.getSpShippingInfo(shippingGroup.generacShippingAccounts, shippingGroup.dealerShippingAccounts);
    return combineLatest([
      this.translationService.translate(data?.selectedShippingAccount == GenShippingAccountType.GENERAC ?
        'checkoutReview.generacPrimaryCarrier' : 'checkoutReview.dealerPrimaryCarrier'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [data?.selectedCarrier],
        } as Card;
      })
    );
  }

  getSpShippingConditionsCard(shippingGroup: GenShippingGroup): Observable<Card> {
    const data = this.getSpShippingInfo(shippingGroup.generacShippingAccounts, shippingGroup.dealerShippingAccounts);
    return combineLatest([
      this.translationService.translate('checkoutReview.shippingConditions'),
    ]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          text: [data?.selectedShippingCondition],
        } as Card;
      })
    );
  }

  getCheckoutStepUrl(stepType: CheckoutStepType | string, shippingGroupName?: string): string | undefined {
    const step = this.checkoutStepService.getCheckoutStep(
      stepType as CheckoutStepType
    );
    if (stepType == this.checkoutStepTypeSpecialHandling && shippingGroupName) {
      return shippingGroupName == GenCartType.FINISHED_GOODS ? 'checkoutFinishedGoods' : 'checkoutServiceParts';
    }
    return step?.routeName;
  }

  getShippingGroupDisplayName(cartType: string): string {
    return this.checkoutService.getShippingGroupDisplayName(cartType);
  }

  private getSpShippingInfo(generacShippingAccounts: GenShippingAccount[], dealerShippingAccounts: GenShippingAccount[]): any {
    let selectedDeliveryOption: GenShippingAccount;
    let selectedShippingAccount: GenShippingAccountType;
    [generacShippingAccounts, dealerShippingAccounts].forEach(carrier => {
      if (carrier) {
        carrier.forEach(shippingAccount => {
          const selectedShippingCondition = shippingAccount.shippingConditions?.find(sc => sc?.selected);
          if (selectedShippingCondition) {
            selectedDeliveryOption = {
              ...shippingAccount,
              shippingConditions: [selectedShippingCondition]
            }
          }
        })
      }
    });
    const selectedCarrier = selectedDeliveryOption?.vendorName || generacShippingAccounts[0].vendorName;
    const selectedShippingCondition = selectedDeliveryOption?.shippingConditions[0].shippingConditionName ||
      generacShippingAccounts[0].shippingConditions[0].shippingConditionName;
    if (selectedDeliveryOption) {
      selectedShippingAccount = !!selectedDeliveryOption?.generacShippingAccount ?
        GenShippingAccountType.GENERAC : GenShippingAccountType.DEALER;
    }
    return {
      selectedShippingAccount,
      selectedCarrier,
      selectedShippingCondition,
    }
  }
}
